import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  FuelPriceIndex_Read_Typeahead,
  FuelSurchargeScheduleType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import React, { Key, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { SelectOption } from '~components/FormFields/Parts/SelectOption';
import { RadioGroupFormField } from '~components/FormFields/RadioGroupFormField';
import Label from '~components/FormFields/v2/Label';
import TextInput from '~components/FormFields/v2/TextInput';
import { nameIdSchemaRequired } from '~constants/regexConst';
import { useFuelPriceIndices } from '~hooks/useFuelPriceIndices/useFuelPriceIndices';
import { ItemNameAndId } from '~types/ItemNameAndId';

const schema = yup.object().shape({
  name: yup.string().required(
    t('form_validation_errors.required', {
      field: t('administration.fuel_surcharge_schedule.form_name_label'),
    }),
  ),
  fuelPriceIndex: nameIdSchemaRequired.required(),
  surchargeType: yup
    .mixed<FuelSurchargeScheduleType>()
    .oneOf(Object.values(FuelSurchargeScheduleType))
    .required(),
});

interface FuelSurchargeScheduleFormData {
  name: string;
  fuelPriceIndex: ItemNameAndId;
  surchargeType: FuelSurchargeScheduleType;
}

interface FuelSurchargeScheduleFormProps {
  onSubmit?: (data: FuelSurchargeScheduleFormData) => void;
  initialValues?: FuelSurchargeScheduleFormData;
}

export interface FuelSurchargeScheduleFormHandler {
  submit: () => Promise<FuelSurchargeScheduleFormData | undefined>;
}

export const FuelSurchargeScheduleForm = React.forwardRef<
  FuelSurchargeScheduleFormHandler,
  FuelSurchargeScheduleFormProps
>(({ onSubmit, initialValues }, ref) => {
  const { getFuelPriceIndicesTypeahead } = useFuelPriceIndices();
  const {
    control,
    formState: { errors },
    handleSubmit,
    trigger,
  } = useForm<FuelSurchargeScheduleFormData>({
    defaultValues: initialValues ?? {
      name: '',
      fuelPriceIndex: { id: '', name: '' },
      surchargeType: FuelSurchargeScheduleType.PERCENTAGE,
    },
    resolver: yupResolver(schema),
  });

  useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        const isValid = await trigger();
        if (!isValid) {
          return undefined;
        }

        const formData = await new Promise<FuelSurchargeScheduleFormData>((resolve) => {
          handleSubmit((data) => {
            resolve(data);
          })();
        });

        return formData;
      },
    }),
    [handleSubmit, trigger],
  );

  const handleFormSubmit = handleSubmit((data) => {
    onSubmit?.(data);
  });

  const fetchFuelPriceIndices = (
    additionalProps: { query?: string; cursor?: string } = {},
  ) =>
    getFuelPriceIndicesTypeahead({
      searchQuery: additionalProps.query,
      cursor: additionalProps.cursor,
    });

  return (
    <Box component="form" onSubmit={handleFormSubmit}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 2,
        }}
      >
        <TextInput
          control={control}
          label={t('administration.fuel_surcharge_schedule.form_name_label')}
          name="name"
          isRequired
          sx={{ gridColumn: 'span 3' }}
        />
        <AutocompleteAsyncFormField
          control={control}
          errors={errors}
          label={t('administration.fuel_surcharge_schedule.fuel_price_index_label')}
          name="fuelPriceIndex"
          getValue={(index) => index.id}
          getLabel={(index) => index.name}
          renderOption={(props, option: FuelPriceIndex_Read_Typeahead, state) => {
            const { key, ...optionProps } = props;

            return (
              <SelectOption
                key={key as Key}
                isCheckEnabled={false}
                getLabel={(index) => index.name}
                inputValue={state?.inputValue ?? ''}
                option={option}
                props={optionProps}
                secondary={
                  option.system && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {t('administration.fuel_price_index.synced_from_eia')}
                    </Typography>
                  )
                }
              />
            );
          }}
          sx={{ gridColumn: 'span 3' }}
          isRequired
          asyncCallback={fetchFuelPriceIndices}
          debounceTime={400}
          defaultValue={initialValues?.fuelPriceIndex}
        />

        <Box sx={{ gridColumn: 'span 3' }}>
          <Label isRequired>
            {t('administration.fuel_surcharge_schedule.rate_type')}
          </Label>
          <RadioGroupFormField
            control={control}
            name="surchargeType"
            list={[
              {
                value: FuelSurchargeScheduleType.PERCENTAGE,
                label: t('administration.fuel_surcharge_schedule.percentage'),
              },
              {
                value: FuelSurchargeScheduleType.PER_UNIT,
                label: t('administration.fuel_surcharge_schedule.per_unit'),
              },
              {
                value: FuelSurchargeScheduleType.PER_LOAD,
                label: t('administration.fuel_surcharge_schedule.per_load'),
              },
            ]}
            sx={{ gridColumn: 'span 3' }}
            errors={errors}
          />
        </Box>
      </Box>
    </Box>
  );
});

FuelSurchargeScheduleForm.displayName = 'FuelSurchargeScheduleForm';
